import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { ResumeService } from 'src/app/core/services/resume.service';
import { BulkResumeUploadComponent } from 'src/app/shared/bulk-resume-upload/bulk-resume-upload.component';
import { UploadResumesComponent } from '../upload-resumes/upload-resumes.component';

declare var $: any;

// Long enough for the per-file tick marks to be seen before leaving the page.
const FILES_UPLOADED_DISPLAY_MS = 3000;

type UploadStage ='idle' | 'files' | 'folders';

interface DroppedContent {
  files: File[];
  folderFiles: File[];
}

@Component({
  selector: 'app-unified-resume-upload',
  templateUrl: './unified-resume-upload.component.html',
  styleUrls: ['./unified-resume-upload.component.css']
})
export class UnifiedResumeUploadComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() jobId;
  @Input() candidateId;
  @Input() controlObject: any;
  @Input() sourceScreen;
  @Input() formalizeActivityPayload;
  @Input() disableSourceTypesFlag = false;
  @Output() cancelCallback = new EventEmitter<void>();
  @Output() candidateProcessedCallback = new EventEmitter<void>();
  @Output() completeCallback = new EventEmitter<void>();
  @Output() filesUploadedCallback = new EventEmitter<void>();

  @ViewChild(UploadResumesComponent) filesUpload: UploadResumesComponent;
  @ViewChild(BulkResumeUploadComponent) foldersUpload: BulkResumeUploadComponent;

  stage: UploadStage = 'idle';
  sourceTypeList: any[] = [];
  sourceType: any;
  uploadDate: Date = new Date();
  dragOver = false;

  private queuedFolderFiles: File[] = [];
  private filesUploaded = false;
  private watchedDropzone: any;
  private filesUploadedTimer: any;

  private readonly onDragOver = (event: DragEvent) => {
    event.preventDefault();
    this.dragOver = true;
  }

  private readonly onDragLeave = (event: DragEvent) => {
    if (!this.host.nativeElement.contains(event.relatedTarget)) {
      this.dragOver = false;
    }
  }

  private readonly onDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    this.readDropped(event.dataTransfer).then(content => this.route(content));
  }

  constructor(
    private host: ElementRef,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private alertsAndNotificationsService: AlertAndNotificationsService,
    private resumeService: ResumeService
  ) { }

  ngOnInit() {
    this.resumeService.getAllSourceTypes((data) => {
      this.sourceTypeList = data || [];
    }, (error) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  ngAfterViewInit() {
    const element: HTMLElement = this.host.nativeElement;
    // Capture phase so a drop on a child's own area (Dropzone) is routed here instead.
    element.addEventListener('dragover', this.onDragOver, true);
    element.addEventListener('dragleave', this.onDragLeave, true);
    element.addEventListener('drop', this.onDrop, true);
    this.initializeDatePicker();
  }

  ngOnDestroy() {
    clearTimeout(this.filesUploadedTimer);
    const element: HTMLElement = this.host.nativeElement;
    element.removeEventListener('dragover', this.onDragOver, true);
    element.removeEventListener('dragleave', this.onDragLeave, true);
    element.removeEventListener('drop', this.onDrop, true);
    this.destroyDatePicker();
  }

  onFilesPicked(input: HTMLInputElement) {
    const files = Array.from(input.files || []);
    input.value = '';
    this.route({ files, folderFiles: [] });
  }

  onFolderPicked(input: HTMLInputElement) {
    const folderFiles = Array.from(input.files || []);
    input.value = '';
    this.route({ files: [], folderFiles });
  }

  onFilesProcessed() {
    this.filesUploaded = true;
    this.candidateProcessedCallback.emit();
    if (this.queuedFolderFiles.length > 0) {
      this.showFolders();
    } else if (!this.hasRejectedFiles()) {
      this.filesUploadedTimer = setTimeout(() => this.filesUploadedCallback.emit(), FILES_UPLOADED_DISPLAY_MS);
    }
  }

  openDatePicker() {
    $('#unifiedUploadDate').datepicker('show');
  }

  private initializeDatePicker() {
    const today = new Date();
    const input = $('#unifiedUploadDate');
    input.datepicker({
      format: 'M d, yyyy',
      endDate: today,
      weekStart: 1,
      autoclose: true
    });
    input.datepicker('setDate', this.uploadDate);
    input.on('changeDate', (e) => {
      if (e.date) {
        this.uploadDate = e.date;
      }
    });
  }

  private destroyDatePicker() {
    $('#unifiedUploadDate').datepicker('destroy');
  }

  private route(content: DroppedContent) {
    if (this.stage === 'folders' && this.foldersUpload && this.foldersUpload.fileUploadInProgressFlag) {
      this.alertsAndNotificationsService.showBannerMessage('An upload is in progress. Wait for it to finish before adding more.', 'warning');
      return;
    }
    // Single-candidate upload keeps the legacy per-file flow; everything else goes through the bulk upload API.
    const sendFilesToBulk = !this.candidateId;
    const folderFiles = sendFilesToBulk ? content.folderFiles.concat(content.files) : content.folderFiles;
    let files = sendFilesToBulk ? [] : content.files;
    if (files.length === 0 && folderFiles.length === 0) {
      this.alertsAndNotificationsService.showBannerMessage('Nothing to upload. Drop resume files or a folder of resumes.', 'warning');
      return;
    }

    if (files.length > 0 && this.stage === 'folders') {
      this.alertsAndNotificationsService.showBannerMessage('Finish the folder upload before adding individual files.', 'warning');
      files = [];
    } else if (files.length > 0 && this.stage === 'files' && this.filesUploaded) {
      this.alertsAndNotificationsService.showBannerMessage('The files were already uploaded. Open Add Candidates again to upload more.', 'warning');
      files = [];
    }
    if (files.length === 0 && folderFiles.length === 0) { return; }

    this.queuedFolderFiles = this.queuedFolderFiles.concat(folderFiles);

    if (files.length > 0) {
      this.showFiles(files);
      if (this.queuedFolderFiles.length > 0) {
        this.alertsAndNotificationsService.showBannerMessage('Files and folders were added. The files upload first, then the folders.', 'info');
      }
    } else if (this.hasPendingFiles()) {
      this.alertsAndNotificationsService.showBannerMessage('The folders will upload after the files.', 'info');
    } else {
      this.showFolders();
    }
  }

  private hasRejectedFiles(): boolean {
    const dropzone = this.filesUpload ? this.filesUpload.resumeUploadDropzone : null;
    return !!dropzone && dropzone.files.some(file => file.status === 'error');
  }

  private hasPendingFiles(): boolean {
    const dropzone = this.stage === 'files' && this.filesUpload ? this.filesUpload.resumeUploadDropzone : null;
    return !!dropzone && dropzone.files.length > 0 && !this.filesUploaded;
  }

  private showFiles(files: File[]) {
    this.leaveIdle('files');
    this.filesUploaded = false;
    this.cdr.detectChanges();
    const dropzone = this.filesUpload.resumeUploadDropzone;
    if (this.watchedDropzone !== dropzone) {
      this.watchedDropzone = dropzone;
      dropzone.on('reset', () => this.zone.run(() => this.onFilesCleared()));
    }
    files.forEach(file => dropzone.addFile(file));
  }

  private showFolders() {
    const folderFiles = this.queuedFolderFiles;
    this.queuedFolderFiles = [];
    this.leaveIdle('folders');
    this.cdr.detectChanges();
    this.foldersUpload.onDirectoriesSelected({ target: { files: folderFiles } });
  }

  onFoldersCleared() {
    if (this.stage === 'folders') {
      this.backToStart();
    }
  }

  private onFilesCleared() {
    if (this.stage !== 'files' || this.filesUploaded) { return; }
    if (this.queuedFolderFiles.length > 0) {
      this.showFolders();
    } else {
      this.backToStart();
    }
  }

  private backToStart() {
    this.stage = 'idle';
    this.filesUploaded = false;
    this.watchedDropzone = null;
    this.cdr.detectChanges();
    this.initializeDatePicker();
  }

  private leaveIdle(stage: UploadStage) {
    if (this.stage === 'idle') {
      this.destroyDatePicker();
    }
    this.stage = stage;
  }

  private async readDropped(dataTransfer: DataTransfer): Promise<DroppedContent> {
    const files: File[] = [];
    const directories: any[] = [];
    // Entries must be taken synchronously; the DataTransfer is cleared after the event.
    Array.from(dataTransfer.items as any).forEach((item: any) => {
      if (item.kind !== 'file') { return; }
      const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
      if (entry && entry.isDirectory) {
        directories.push(entry);
      } else {
        const file = item.getAsFile();
        if (file) { files.push(file); }
      }
    });
    const folderFiles = ([] as File[]).concat(...await Promise.all(directories.map(entry => this.readDirectory(entry, ''))));
    return { files, folderFiles };
  }

  private readDirectory(entry: any, parentPath: string): Promise<File[]> {
    const path = parentPath + entry.name + '/';
    const reader = entry.createReader();
    const collected: any[] = [];
    // readEntries returns results in pages, so keep reading until it comes back empty.
    const readPage = (): Promise<any[]> => new Promise(resolve => {
      reader.readEntries(page => {
        if (page.length === 0) {
          resolve(collected);
        } else {
          collected.push(...page);
          resolve(readPage());
        }
      }, () => resolve(collected));
    });
    return readPage().then(children => Promise.all(children.map(child => child.isDirectory
      ? this.readDirectory(child, path)
      : this.readFile(child, path)
    ))).then(results => ([] as File[]).concat(...results));
  }

  private readFile(entry: any, parentPath: string): Promise<File[]> {
    return new Promise(resolve => {
      entry.file(file => {
        Object.defineProperty(file, 'webkitRelativePath', { value: parentPath + file.name });
        resolve([file]);
      }, () => resolve([]));
    });
  }
}
